'use client';

import { useTranslations } from 'next-intl';
import { MetricCard } from '@/components/ui/metric-card';
import type { PlayerProfileData } from '@/models/playerModel';

interface ProfileStatsRowProps {
  player: { current_rating: number | null };
  stats: PlayerProfileData;
}

export default function ProfileStatsRow({ player, stats }: ProfileStatsRowProps) {
  const t = useTranslations('Profile');
  const tUtils = useTranslations('Utils');

  const ratingValue = player.current_rating
    ? Math.round(player.current_rating)
    : tUtils('notAvailable');

  // Calculate rating delta from most recent match
  const recentMatch = stats.matchHistory.find((m) => !m.isAdjustment && m.myDelta !== null);
  const ratingDelta = recentMatch?.myDelta ?? null;

  const winRateValue = `${stats.winRate.toFixed(0)}%`;

  // Only show rank card if player actually has a rank
  const hasRank = !!stats.currentRank;
  let rankSubtitle: string | undefined;
  if (hasRank && stats.leaderboardSize) {
    const percentile = Math.round((stats.currentRank! / stats.leaderboardSize) * 100);
    rankSubtitle = t('topPercentile', { percent: percentile });
  }

  // One compact strip. Matches played lives here too (it used to be a second
  // row of cards under the chart). Four cards go 2×2 on phones; three fit in
  // one row from 380px (below that a 4-digit rating wraps), avoiding an orphan.
  const cols = hasRank ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 min-[380px]:grid-cols-3';

  return (
    <div className={`grid gap-3 sm:gap-4 ${cols}`}>
      <MetricCard
        title={t('skillRating')}
        value={ratingValue}
        change={
          ratingDelta !== null
            ? { value: Math.round(ratingDelta), isPositive: ratingDelta >= 0 }
            : undefined
        }
      />
      <MetricCard
        title={t('winRate')}
        value={winRateValue}
        subtitle={`${stats.wins}W / ${stats.losses}L`}
      />
      {hasRank && (
        <MetricCard
          title={t('currentRank')}
          value={`#${stats.currentRank}`}
          subtitle={rankSubtitle || stats.federationName || undefined}
        />
      )}
      <MetricCard title={t('matchesPlayed')} value={stats.totalMatches} />
    </div>
  );
}
